import type { FinishPatrolRequest, Patrol } from '@local/common-lib';
import * as Sentry from '@sentry/react';
import { AxiosError } from 'axios';
import { useFormik } from 'formik';
import { DateTime } from 'luxon';
import { useSnackbar } from 'notistack';
import type { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Issue } from 'validata';
import { useAuthenticated } from '../../../utils/auth/index.js';
import type { ExistingEntityViewProps } from '../../../utils/types.js';
import { formikCheck } from '../../../utils/validation.js';
import type { FinishPatrolForm } from './finish.view.js';
import {
  FinishPatrolView,
  finishPatrolFormContract,
  finishPatrolFormContractNoVehicle,
} from './finish.view.js';

export interface Props extends ExistingEntityViewProps<Patrol.Type> {
  setShowFinish: (value: boolean) => void;
}

const toFuelLevelPercent = (value: number): number | undefined => {
  switch (value) {
    case 0:
      return undefined;
    case 1:
      return 0.1;
    case 5:
      return 0.9;
    default:
      return (value - 1) / 4;
  }
};

export const FinishPatrolContent: FC<Props> = ({
  data: patrol,
  id,
  setShowFinish,
}) => {
  const auth = useAuthenticated();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const startPatrolForm = useFormik<FinishPatrolForm>({
    initialValues: {
      emailEcc: Boolean(
        patrol.eccLogOnResponse?.policeEventNumber ||
          patrol.started?.policeEventNumber,
      ),
      finished: DateTime.local().toISO(),
      guestsPresent: patrol.started?.guestsPresent ?? [],
      notes: '',
      patrollersPresent: patrol.started?.patrollersPresent ?? [],
      vehicleFinishOdometer: undefined,
      vehicleFuelLevel: 0,
    },
    onReset: () => setShowFinish(false),
    onSubmit: async (values) => {
      const body: FinishPatrolRequest = {
        emailEcc: values.emailEcc,
        finished: values.finished
          ? DateTime.fromISO(values.finished).toJSDate()
          : new Date(),
        guestsPresent: values.guestsPresent,
        location: values.location,
        notes: values.notes || undefined,
        patrollersPresent: values.patrollersPresent,
        vehicle: patrol.started?.vehicle
          ? {
              finishOdometer: values.vehicleFinishOdometer ?? 0,
              fuelLevel: toFuelLevelPercent(values.vehicleFuelLevel ?? 0),
            }
          : undefined,
      };
      try {
        await auth.axios.post(
          `/api/v1/patrol/${encodeURIComponent(id)}/finish`,
          body,
        );
        enqueueSnackbar('Patrol finished', { variant: 'success' });
        navigate(`/patrol/${encodeURIComponent(id)}`, { replace: true });
      } catch (err: unknown) {
        Sentry.captureException(err);
        if (err instanceof AxiosError) {
          if (err.response?.status === 400) {
            const data = err.response.data as { issues: Issue[] };
            if ('issues' in data) {
              const issues = data.issues;
              console.error('Finish patrol: validation issues', issues);
              if (issues.length === 1) {
                const issue = issues[0];
                switch (issue.reason) {
                  case 'already-finished':
                    enqueueSnackbar('Patrol has already been finished', {
                      variant: 'error',
                    });
                    break;
                  case 'not-started':
                    enqueueSnackbar('Patrol has not been started', {
                      variant: 'error',
                    });
                    break;
                  case 'not-finalized':
                    enqueueSnackbar(
                      'Patrol cannot be finished until it has been finalized',
                      {
                        variant: 'error',
                      },
                    );
                    break;
                  case 'not-assigned-to-user':
                    enqueueSnackbar('Patrol is not assigned to you', {
                      variant: 'error',
                    });
                    break;
                }

                navigate(`/patrol/${encodeURIComponent(id)}`, {
                  replace: true,
                });
                return;
              }
            }
          }
        }
        if (err instanceof Error) {
          enqueueSnackbar(`Unable to update: ${err.message}`, {
            variant: 'error',
          });
        } else {
          throw err;
        }
      }
    },
    validate: async (value) => {
      const { vehicleFinishOdometer, vehicleFuelLevel, ...rest } = value;
      const a = patrol.started?.vehicle
        ? formikCheck(
            finishPatrolFormContract(patrol.started.vehicle.startOdometer ?? 0),
            {
              vehicleFinishOdometer,
              vehicleFuelLevel,
              ...rest,
            },
          )
        : formikCheck(finishPatrolFormContractNoVehicle, rest);
      console.log(await a);
      return a;
    },
    validateOnBlur: true,
  });

  return (
    <FinishPatrolView finishPatrolForm={startPatrolForm} patrol={patrol} />
  );
};
